import { apm } from '@elastic/apm-rum';
import { UserManager } from 'oidc-client';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CircularProgressCentered } from '../components/circular-progress-centered/circular-progress-centered';
import { getTenant } from '../tenant-support/get-tenant';
import { session } from '../utilities/session-storage';
import { win } from '../utilities/window';
import { initializeAuthentication } from './auth-initialize';
import { AuthWrapper } from './auth-wrapper';
import { BffSession, bffLoginUrl, currentUrl, fetchBffSession, sendSessionCookieWithHttpRequests } from './bff-session';
import { BffSessionWrapper } from './bff-session-wrapper';
import { endCookieSessionWhenLoggedOutElsewhere, isLoggedOutSinceSignIn, showLoggedOut } from './logged-out-elsewhere';

export interface AuthenticationProps {
  children: JSX.Element;
  renderError: () => JSX.Element;
  renderFkoErrorPage: () => JSX.Element;
}

type LoginMode = { kind: 'resolving' } | { kind: 'legacy'; userManager: UserManager | null } | { kind: 'cookie'; session: BffSession };

const userProvisioningMessageShownKey = 'fasit-user-provisioning-message-shown';

function initializeLegacyAuthentication() {
  return initializeAuthentication({
    clientId: 'jobcenter-desktop-frontend',
    apiName: 'jobcenter-desktop-bff',
    tenant: getTenant().name,
  });
}

function onCookieSessionEnded() {
  if (isLoggedOutSinceSignIn()) {
    showLoggedOut();
    return;
  }
  win.location.assign(bffLoginUrl(currentUrl()));
}

function initializeCookieSession(bffSession: BffSession) {
  const { user } = bffSession;
  if (!user) {
    return;
  }

  sendSessionCookieWithHttpRequests(onCookieSessionEnded);
  endCookieSessionWhenLoggedOutElsewhere();
  try {
    apm.setUserContext({ id: user.id, username: user.name, email: user.sub });
  } catch {
    // Not much we can do here
  }
}

/**
 * Picks the login the tenant uses before anything calls the BFF: the BFF's cookie session where
 * UseApexStsLogin_enableBffCookieSession is on, otherwise the legacy login in the browser.
 */
export function Authentication(props: AuthenticationProps) {
  const { children, renderError, renderFkoErrorPage } = props;
  const [loginMode, setLoginMode] = useState<LoginMode>({ kind: 'resolving' });
  const history = useHistory();

  useEffect(() => {
    fetchBffSession().then(bffSession => {
      if (!bffSession.cookieSessionEnabled) {
        setLoginMode({ kind: 'legacy', userManager: initializeLegacyAuthentication() });
        return;
      }

      initializeCookieSession(bffSession);
      const message = bffSession.user?.userProvisioningMessage;
      if (message && !session.getItem(userProvisioningMessageShownKey)) {
        session.setItem(userProvisioningMessageShownKey, 'true');
        history.replace('/fko-error-page', { message, returnPathFromCallback: win.location.pathname + win.location.search + win.location.hash });
      }
      setLoginMode({ kind: 'cookie', session: bffSession });
    });
  }, [history]);

  if (loginMode.kind === 'resolving') {
    return <CircularProgressCentered />;
  }

  if (loginMode.kind === 'legacy') {
    return (
      <AuthWrapper userManager={loginMode.userManager} renderError={renderError} renderFkoErrorPage={renderFkoErrorPage}>
        {children}
      </AuthWrapper>
    );
  }

  return (
    <BffSessionWrapper user={loginMode.session.user} renderError={renderError}>
      {children}
    </BffSessionWrapper>
  );
}
